import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { GraduationCap, ChevronDown, ChevronUp } from 'lucide-react';
import { FadeIn } from '@/components/motion';
import { useCertificationInterest } from '@/lib/hooks/use-certification-interest';

const TIMEZONE_OPTIONS = [
  'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific',
  'Europe/London', 'Europe/Berlin', 'Europe/Paris',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo',
  'Australia/Sydney', 'Other',
];

const SCHEDULE_OPTIONS = [
  'Weekday mornings', 'Weekday afternoons', 'Weekday evenings',
  'Weekends', 'Flexible', 'Other',
];

export function CertificationInterestToggle() {
  const { interest, isInterested, isLoading, updateInterest, isUpdating } = useCertificationInterest();
  const [showDetails, setShowDetails] = useState(false);
  const [timezone, setTimezone] = useState(interest?.timezone || '');
  const [schedule, setSchedule] = useState(interest?.preferred_schedule || '');
  const [note, setNote] = useState(interest?.note || '');

  const handleToggle = async (checked: boolean) => {
    await updateInterest({
      is_interested: checked,
      timezone: timezone || null,
      preferred_schedule: schedule || null,
      note: note || null,
    });
    if (checked) setShowDetails(true);
  };

  const handleSaveDetails = async () => {
    await updateInterest({
      is_interested: true,
      timezone: timezone || null,
      preferred_schedule: schedule || null,
      note: note || null,
    });
  };

  if (isLoading) return null;

  return (
    <FadeIn>
      <Card className="border-color-4/20 bg-gradient-to-r from-color-4/5 to-transparent">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-color-4/10 flex items-center justify-center">
                <GraduationCap className="h-5 w-5 text-color-4" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Open to Certification Cohorts</h3>
                <p className="text-xs text-muted-foreground">
                  Let other Geniuses know you want to team up for the Claude Certified Architect program
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={isInterested}
                onCheckedChange={handleToggle}
                disabled={isUpdating}
              />
              {isInterested && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-xs"
                >
                  {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </div>

          {isInterested && showDetails && (
            <FadeIn className="mt-4 pt-4 border-t space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">Your Timezone</Label>
                  <Select value={timezone} onValueChange={setTimezone}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONE_OPTIONS.map(tz => (
                        <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Preferred Schedule</Label>
                  <Select value={schedule} onValueChange={setSchedule}>
                    <SelectTrigger>
                      <SelectValue placeholder="When are you available?" />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHEDULE_OPTIONS.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Short Note (optional)</Label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="What are you looking for in a cohort? Any specific skills or experience you bring?"
                  className="resize-none h-20"
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground text-right">{note.length}/500</p>
              </div>
              <Button
                size="sm"
                onClick={handleSaveDetails}
                disabled={isUpdating}
              >
                {isUpdating ? 'Saving...' : 'Save Preferences'}
              </Button>
            </FadeIn>
          )}
        </CardContent>
      </Card>
    </FadeIn>
  );
}
